import clsx from "clsx";
import { FormApi, FormState, ValidationErrors } from "final-form";
import _ from "lodash";
import React from "react";
import { Form, FormSpy } from "react-final-form";
import { useTranslation } from "react-i18next";
import { PermissionService } from "src/services/permissionService/permissionService";
import useDebounce from "src/ui/hooks/useDebounce";
import { getOrganizationUrlValidationError } from "src/ui/utils/validityChecker";
import { makeStyles } from "tss-react/mui";

import {
    useCurrentSubscriptionExpired,
    useCurrentWorkspace,
    useIsWorkspaceFeatureLimitReached,
} from "@dashboard/workspaces/api/helpers";
import { Box } from "@mui/material";
import { useCreateProductMutation, useUpdateProductMutation } from "@products/api";
import { useCurrentWorkspaceProducts } from "@products/api/helpers";
import { Product } from "@products/types";
import CompareService from "@services/compareService/compareService";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import { showSuccessToast } from "@shared/toasts/Toasts";

import AdvanceModeSection from "./components/AdvanceModeSection/AdvanceModeSection";
import { ChangeSection } from "./components/ChangeSection";
import { ConfigurationModal } from "./components/ConfigurationDialog/ConfigurationDialog";
import { MainButton } from "./components/MainButton/MainButton";
import { ProductBuilderImage } from "./components/ProductBuilderImage/ProductBuilderImage";
import { Recipes } from "./components/Recipes";
import { getInitialValues, isHeadlessModuleAdded, isKioskModuleAdded, mutators } from "./ProductBuilder.utils";

const useStyles = makeStyles()((theme) => ({
    container: {
        display: "flex",
        gap: theme.spacing(5),
        paddingTop: theme.spacing(5),
        "@media (max-width: 768px)": {
            flexDirection: "column",
        },
    },
    contentBox: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
    },
    buttonBox: {
        display: "flex",
        justifyContent: "space-between",
        marginTop: theme.spacing(5),
    },
    buttonSubBox: { display: "flex", gap: theme.spacing(1) },
    imageBox: {
        flex: 1,
        justifyContent: "center",
        display: "flex",
        alignItems: "center",
    },
    toogleIcon: { fontSize: 18, color: theme.palette.emteria.text.secondary },
}));

// How long the form stays quiet before an edit is saved in the background. Long enough to sit out a
// normal pause between words, so typing a description does not produce a save (and a toast) mid-sentence.
const autosaveDebounceMs = 1600;

type Props = {
    product: Product | undefined;
    setCurrentProductId: (value: number) => void;
    onCancelCreation: () => void | Promise<void>;
};

export const ProductBuilder = (props: Props) => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [configurationModal, setConfigurationModal] = React.useState(false);
    const [isAdvancedMode, setIsAdvancedMode] = React.useState(false);

    const { classes } = useStyles();
    const { t } = useTranslation(["common", "pages"]);

    const { data: workspace } = useCurrentWorkspace();
    const initialValues = React.useMemo(() => getInitialValues(props.product, workspace?.id), [props.product, workspace]);

    const { data: products = [] } = useCurrentWorkspaceProducts();

    const [createProduct] = useCreateProductMutation();
    const [updateProduct] = useUpdateProductMutation();

    const isProductLimitReached = useIsWorkspaceFeatureLimitReached("ProductLimit");
    const isBuildLimitReached = useIsWorkspaceFeatureLimitReached("BuildLimit");
    const codenameAndroidVersion = products.map((item) => item.codename + item.androidVersion);
    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");
    const isExpired = useCurrentSubscriptionExpired();
    const isCreationProcess = !props.product?.id;

    const onCreate = async (values: Product) => {
        setIsLoading(true);
        const fullCodename = workspace?.guid ? `${workspace.guid}_${values.codename}` : values.codename;
        const result = await createProduct({ ...values, codename: fullCodename });
        if (!("error" in result)) {
            props.setCurrentProductId(result.data.id);
            showSuccessToast(t("pages:productPage.toast.successfullyCreatedProduct"));
        }
        setIsLoading(false);
    };
    const onUpdate = async (values: Product | undefined) => {
        if (!values) {
            return;
        }

        setIsLoading(true);
        const result = await updateProduct({ ...values, codename: props.product?.codename });
        if (!("error" in result)) {
            showSuccessToast(t("pages:productPage.toast.successfullySavedProduct"));
        }
        setIsLoading(false);
    };
    const onBuild = (_values: Product) => {
        setConfigurationModal(true);
    };
    const onSubmit = async (values: Product) => {
        const difference = new CompareService(props.product, values).hasDifferences;

        if (isCreationProcess) {
            await onCreate(values);
        } else if (!isCreationProcess && difference) {
            await onUpdate(values);
        } else {
            onBuild(values);
        }
    };
    const onValidate = async (values: Product) => {
        const errors: Record<string, string> = {};

        if (!values.customBuildConfig?.productName?.trim()) {
            errors["customBuildConfig.productName"] = t("pages:productPage.input.productName.validation.required");
        }

        if (!values.codename?.trim()) {
            errors.codename = t("pages:productPage.input.codename.validation.required");
        }

        const isCodenameDirty = values.codename !== props.product?.codename;
        const isAndroidVersionDirty = values.androidVersion !== props.product?.androidVersion;
        const isCodenameAndAndroidVersionDirty = isCodenameDirty || isAndroidVersionDirty;

        const codenames = workspace?.guid
            ? codenameAndroidVersion.map((item) => item.replace(new RegExp(`^${workspace.guid}_`), ""))
            : codenameAndroidVersion;

        const isProductExists = codenames.includes(values.codename + values.androidVersion);

        if (isCodenameAndAndroidVersionDirty && isProductExists) {
            errors.codename = t("pages:productPage.input.codename.validation.duplicate");
            errors.androidVersion = t("pages:productPage.input.androidVersion.validation.duplicate");
        }

        const documentationUrlError = getOrganizationUrlValidationError(values.documentationUrl?.trim() ?? "");
        if (documentationUrlError) {
            errors.documentationUrl = t(`common:validation.url.${documentationUrlError}`);
        }

        return errors;
    };
    const handleFormChange = async (state: FormState<Product>, form: FormApi<Product>) => {
        if (state.dirty && !state.valid && state.errors) {
            Object.keys(state.errors).forEach((key) => {
                form.mutators.setTouched(key);
            });
        }
        if (state.pristine && !state.valid) {
            form.restart();
        }
        if (!isCreationProcess && state.valid && state.dirty) {
            await onUpdate(state.values);
        }
    };

    const onChange = useDebounce(
        (state: FormState<Product>, form: FormApi<Product>) => {
            void handleFormChange(state, form);
        },
        autosaveDebounceMs,
        // Navigating away right after an edit must save it, not silently drop it.
        { flushOnUnmount: true },
    );

    const creationLockTooltip = React.useMemo(() => {
        if (!isCreationProcess) {
            return "";
        }
        if (isProductLimitReached) {
            return t("pages:productPage.tooltip.maxProductLimitReached");
        }
        if (isExpired) {
            return t("pages:productPage.tooltip.subscriptionExpired");
        }
        if (cannotManageProducts) {
            return t("pages:productPage.tooltip.noPermissionToManageProduct");
        }
        return "";
    }, [cannotManageProducts, isProductLimitReached, isCreationProcess, isExpired, t]);

    const isCreationDisabled = isCreationProcess && (isProductLimitReached || cannotManageProducts);

    const getCreateButtonTooltip = () => {
        if (!creationLockTooltip) {
            return {
                title: "",
                hide: true,
            };
        }

        return {
            title: creationLockTooltip,
            hide: false,
        };
    };
    const getToogleButtonTooltip = (errors: ValidationErrors) => {
        if (isExpired) {
            return {
                title: t("pages:productPage.tooltip.subscriptionExpired"),
                hide: false,
            };
        }
        if (cannotManageProducts) {
            return {
                title: t("pages:productPage.tooltip.noPermissionToManageProduct"),
                hide: false,
            };
        }

        const error = Object.values<string>({ ...errors })[0];
        if (error) {
            return {
                title: error,
                hide: false,
            };
        }

        return {
            title: t("pages:productPage.tooltip.toggleAdvancedEditor"),
            hide: false,
        };
    };

    return (
        <Form<Product>
            onSubmit={onSubmit}
            initialValues={initialValues}
            // Compare by value, not by reference. Every refetch of the product list rebuilds these objects, and
            // the default shallow compare then treats an unchanged product as new and resets the fields under
            // the user — losing whatever was typed since. Starting a build does exactly that, from the table below.
            initialValuesEqual={_.isEqual}
            // Now that documentationUrl/description are sent and stored verbatim (see #1191), a completed
            // autosave never returns a value that differs from what the user already has in the form, so
            // it's safe to keep dirty fields on reinitialize — otherwise characters typed during an
            // in-flight save would be overwritten once that save's response reinitializes the form.
            keepDirtyOnReinitialize
            validate={onValidate}
            mutators={mutators}
            render={({ handleSubmit, values, form, errors }) => {
                return (
                    <form onSubmit={handleSubmit}>
                        <FormSpy<Product>
                            subscription={{ values: true, valid: true, dirty: true, errors: true, pristine: true }}
                            onChange={(state) => onChange(state, form)}
                        />
                        <Box className={classes.container}>
                            {isAdvancedMode && props.product ? (
                                <AdvanceModeSection initialValue={props.product} setIsAdvancedMode={setIsAdvancedMode} />
                            ) : (
                                <React.Fragment>
                                    <Box className={classes.contentBox}>
                                        <Recipes
                                            product={props.product}
                                            isCreationDisabled={isCreationDisabled}
                                            creationLockTooltip={creationLockTooltip}
                                        />
                                        <Box className={classes.buttonBox}>
                                            <Box className={classes.buttonSubBox}>
                                                {props.product ? (
                                                    <SecondaryButton
                                                        size="small"
                                                        data-testid="advance-mode-switch"
                                                        onClick={() => setIsAdvancedMode((prev) => !prev)}
                                                        tooltipProps={getToogleButtonTooltip(errors)}
                                                        disabled={
                                                            cannotManageProducts || !!Object.values<string>({ ...errors })[0]
                                                        }
                                                    >
                                                        <a className={clsx("fa-solid fa-code", classes.toogleIcon)} />
                                                    </SecondaryButton>
                                                ) : null}
                                                {isCreationProcess && products.length > 0 ? (
                                                    <SecondaryButton
                                                        size="large"
                                                        data-testid="cancel-creation"
                                                        onClick={() => void props.onCancelCreation()}
                                                        disabled={isLoading}
                                                    >
                                                        {t("common:button.cancel")}
                                                    </SecondaryButton>
                                                ) : null}
                                            </Box>
                                            <Box className={classes.buttonSubBox}>
                                                {!isCreationProcess && props.product ? (
                                                    <ChangeSection product={props.product} />
                                                ) : null}

                                                {isCreationProcess ? (
                                                    <PrimaryButton
                                                        type="submit"
                                                        size="large"
                                                        disabled={isLoading || cannotManageProducts || isProductLimitReached}
                                                        loading={isLoading}
                                                        tooltipProps={getCreateButtonTooltip()}
                                                    >
                                                        {t("common:button.create")}
                                                    </PrimaryButton>
                                                ) : null}
                                                {!isCreationProcess && props.product ? (
                                                    <MainButton
                                                        product={props.product}
                                                        isLoading={isLoading}
                                                        cannotManageProducts={cannotManageProducts}
                                                        isExpired={isExpired}
                                                        isBuildLimitReached={isBuildLimitReached}
                                                        error={Object.values<string>({ ...errors })[0]}
                                                    />
                                                ) : null}
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box className={classes.imageBox}>
                                        <ProductBuilderImage
                                            nxpHwChecked={true}
                                            qcomHwChecked={!!values?.customBuildConfig?.rootDevice?.startsWith("rpi")}
                                            customHwChecked={!!values?.customBuildConfig?.rootDevice?.startsWith("caas")}
                                            headlessChecked={isHeadlessModuleAdded(values)}
                                            kioskChecked={isKioskModuleAdded(values)}
                                            customUiChecked={false}
                                            cloudChecked={false}
                                            fleetChecked={false}
                                            fleetAnalyticsChecked={false}
                                        />
                                    </Box>
                                </React.Fragment>
                            )}
                        </Box>
                        {props.product ? (
                            <ConfigurationModal
                                product={props.product}
                                open={configurationModal}
                                onClose={() => setConfigurationModal(false)}
                            />
                        ) : null}
                    </form>
                );
            }}
        />
    );
};
